import React, { useState, useEffect } from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import ContentEditor from "aldoo-ra/CMS/content-editor"
import { showNotification } from "aldoo-ra/Notification"
import DatabaseModelTypeResolver from "aldoo-ra/CMS/database-model-type-resolver"
import { PlusCircleIcon, TrashIcon, PencilIcon, ArrowUturnLeftIcon } from "@heroicons/react/24/outline"
import Tooltip from "aldoo-ra/Tooltip"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import Icon from "aldoo-ra/Icon"
import StoreProductFormatter from "aldoo-ra/CMSModels/StoreProduct/formatters/store-product-formatter"
import { priceDisplayFormat } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import { twMerge } from "tailwind-merge"
import { getStoreProductTypeOptions, getProductTypeColor } from "./store-product-utils"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"
import StoreProductRestoreDialog from "./RestoreDialog"
import { url } from "aldoo-ra/CMS/url"

import apiInterface from "aldoo-ra/CMSModels/StoreProduct/api"
import { api } from "aldoo-ra/API"
const {
  Get: GetStoreProducts,
  Save: SaveStoreProduct,
  SoftDelete: SoftDeleteProduct,
} = apiInterface


export default function ProductBrowser() {
  const { getType } = DatabaseModelTypeResolver()
  const StoreProduct = getType("StoreProduct")
  const StorePrice = getType("StorePrice")

  const [selectedInstance, setSelectedInstance] = useState(null)
  const [selectedPrice, setSelectedPrice] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [platformFilter, setPlatformFilter] = useState([])
  const [typeFilter, setTypeFilter] = useState([])
  const [productTypeOptions, setProductTypeOptions] = useState([])
  const [showRestoreDialog, setShowRestoreDialog] = useState(false)

  // Load product types from ParamIO
  useEffect(() => {
    const loadProductTypes = async () => {
      try {
        const options = await getStoreProductTypeOptions()
        setProductTypeOptions(options)
      } catch (error) {
        console.error('Failed to load product types:', error)
        // Fallback to default options
        setProductTypeOptions([
          { value: "purchase", name: "Purchase" },
          { value: "subscription", name: "Subscription" },
          { value: "license", name: "License" },
          { value: "bundle", name: "Bundle" },
        ])
      }
    }
    loadProductTypes()
  }, [])

  const getPlatformFilter = () => {
    if (!platformFilter || !platformFilter.length) return {}

    const selectedPlatforms = platformFilter.map((t) => t.value)

    // Query products where price.price_model contains real_product_id with matching platforms
    return {
      "price.price_model": {
        $elemMatch: {
          "type": "real",
          "real_product_id": {
            $elemMatch: {
              "platform": { $in: selectedPlatforms }
            }
          }
        }
      }
    }
  }

  const getTypeFilter = () => {
    if (!typeFilter || !typeFilter.length) return {}
    return { type: { $in: typeFilter.map((t) => t.value) } }
  }

  const handleProductClick = (item) => {
    setSelectedInstance(item)
  }

  const createNewProduct = () => {
    // Initialize new product with defaults from StoreProduct model
    const newProduct = {}
    StoreProduct.fields.forEach((field) => {
      if (field.default !== undefined) {
        newProduct[field.id] = field.default
      }
    })
    setSelectedInstance(newProduct)
  }


  const handlePriceClick = async (item) => {
    if (item.price) {
      // Fetch the full price object with price_model populated
      try {
        const crudAPI = api('crud')
        const priceId = typeof item.price === 'object' ? item.price._id : item.price

        const response = await crudAPI({
          get: {
            model: 'StorePrice',
            match: { _id: priceId },
            limit: 1
          }
        })

        if (Array.isArray(response) && response.length > 0) {
          const loadedPrice = response[0]

          // If this is a purchase product, normalize price model
          const productType = typeof item.type === 'object' ? item.type.value : item.type
          if (productType === 'purchase' && loadedPrice.price_model && Array.isArray(loadedPrice.price_model)) {
            loadedPrice.price_model = loadedPrice.price_model.map(model => ({
              ...model,
              auto_renew: false,
              duration: 0
            }))
          }

          setSelectedPrice({
            ...loadedPrice,
            _parentProduct: item // Keep reference to parent product for saving
          })
        } else {
          showNotification({
            message: "Failed to load price data",
            className: "bg-admin_error text-white",
          })
        }
      } catch (error) {
        console.error('Failed to fetch price:', error)
        showNotification({
          message: "Failed to load price data",
          className: "bg-admin_error text-white",
        })
      }
    } else {
      // Create empty price with default price model
      // Start with virtual type to avoid platform ID validation, user can change to real later
      setSelectedPrice({
        price_model: [
          {
            type: 'virtual',
            currency: 'USD',
            amount: 0,
            duration: 'one-time-payment',
            trial_duration: 'no-trial',
            auto_renew: false,
            primary: true,
            requires_payment_method: false,
            real_product_id: [],
            phases: []
          }
        ],
        _parentProduct: item
      })
    }
  }

  const handlePriceSave = async (updatedPrice) => {
    try {
      // Remove the parent product reference before saving
      const { _parentProduct, ...priceToSave } = updatedPrice

      // If this is for a purchase product, enforce purchase-specific constraints
      if (_parentProduct) {
        const productType = typeof _parentProduct.type === 'object'
          ? _parentProduct.type.value
          : _parentProduct.type

        if (productType === 'purchase') {
          // For purchase products, enforce:
          // 1. auto_renew must be false
          // 2. duration must be set to 0 (one-time payment)
          if (priceToSave.price_model && Array.isArray(priceToSave.price_model)) {
            priceToSave.price_model = priceToSave.price_model.map(model => ({
              ...model,
              auto_renew: false,
              duration: 0
            }))
          }
        }
      }

      // Create the CRUD API instance
      const crudAPI = api('crud')

      // Determine if this is a create or update operation
      const isNewPrice = !priceToSave._id
      const operation = isNewPrice ? 'create' : 'update'

      // Save the price using CRUD
      const response = await crudAPI({
        [operation]: {
          model: 'StorePrice',
          data: priceToSave
        }
      })

      if (response.result) {
        const savedPrice = response.result

        // If this was a new price, update the parent product to link to it
        if (isNewPrice && _parentProduct) {
          const productCrudAPI = api('crud')
          const linkResponse = await productCrudAPI({
            update: {
              model: 'StoreProduct',
              data: {
                _id: _parentProduct._id,
                price: savedPrice._id
              }
            }
          })

          if (linkResponse.error) {
            showNotification({
              message: `Price created but failed to link to product: ${linkResponse.error}`,
              className: "bg-admin_error text-white",
            })
          }
        }

        showNotification({
          message: isNewPrice ? "Price created and linked successfully" : "Price updated successfully",
          className: "bg-admin_success text-white",
        })
        setRefreshKey((prev) => prev + 1)
        // Update the selectedPrice to reflect the saved value
        setSelectedPrice({ ...savedPrice, _parentProduct })

        // Update selectedInstance with the new/updated price reference
        if (selectedInstance && _parentProduct) {
          // Fetch the updated product to get the full price data
          const productCrudAPI = api('crud')
          const productResponse = await productCrudAPI({
            get: {
              model: 'StoreProduct',
              match: { _id: _parentProduct._id },
              populate: ['price', 'licenseModel'],
              limit: 1
            }
          })

          if (Array.isArray(productResponse) && productResponse.length > 0) {
            setSelectedInstance(productResponse[0])
          } else {
            // Fallback: just update the price ID
            setSelectedInstance(prev => ({
              ...prev,
              price: savedPrice._id || savedPrice
            }))
          }
        }

        return response
      } else if (response.error) {
        // Parse validation error and provide user-friendly message
        let errorMessage = response.error

        // Check for price_model validation errors
        if (errorMessage.includes('price_model') && errorMessage.includes('amount') && errorMessage.includes('required')) {
          errorMessage = "Please add a regular price with amount, currency, and type"
        }

        showNotification({
          message: errorMessage,
          className: "bg-admin_error text-white",
        })
        return null
      }
    } catch (error) {
      console.error('🔴 Save error:', error)
      showNotification({
        message: error.message || "Failed to save price",
        className: "bg-admin_error text-white",
      })
      return null
    }
  }

  const handleItemDelete = (item) => {
    showPromptDialog({
      title: `Confirm Delete`,
      message: `Are you sure you want to delete ${item.price_id}?`,
      confirmText: "Delete",
      onConfirm: async () => {
        // Handle delete action
        const response = await SoftDeleteProduct(item)

        if (response.error) {
          showNotification({
            message: response.error,
            className: "bg-admin_error text-white",
          })
          return
        } else {
          showNotification({
            message: `Product ${
              !item._id ? "created" : "updated"
            } successfully`,
            className: "bg-admin_primary text-white",
          })
          setRefreshKey((prev) => prev + 1)
        }
      },
    })
  }

  //Save a price item
  const handleItemSave = async (item) => {
    const isNewProduct = !item._id

    // If this is a new product (no _id), show confirmation dialog first
    if (isNewProduct) {
      const confirmed = await new Promise((resolve) => {
        showPromptDialog({
          title: "Create Store Product",
          message: "Once a store product is created its Product ID can't be changed. You can always create a new one if needed.",
          confirmText: "Proceed",
          cancelText: "Go Back",
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })

      if (!confirmed) {
        return null
      }

      // For new products, create a default price if none exists
      if (!item.price) {
        const crudAPI = api('crud')
        const defaultPrice = {
          price_model: [
            {
              type: 'virtual',
              currency: 'USD',
              amount: 0,
              duration: 0, // Number for one-time payment
              trial_duration: 0, // Number for no trial
              auto_renew: false,
              primary: true,
              requires_payment_method: false,
              real_product_id: [],
              phases: []
            }
          ]
        }

        const priceResponse = await crudAPI({
          create: {
            model: 'StorePrice',
            data: defaultPrice
          }
        })

        if (priceResponse.result) {
          const priceId = priceResponse.result._id || priceResponse.result.id || priceResponse.result
          item.price = priceId
        } else {
          showNotification({
            message: `Failed to create default price: ${priceResponse.error}`,
            className: "bg-admin_error text-white",
          })
          return null
        }
      }
    }

    //save the item
    const response = await SaveStoreProduct(item)

    if (response.error) {
      showNotification({
        message: response.error,
        className: "bg-admin_error text-white",
      })
      return null
    } else {
      showNotification({
        message: `Product ${isNewProduct ? "created" : "updated"} successfully`,
        className: "bg-admin_primary text-white",
      })
      setRefreshKey((prev) => prev + 1)
    }

    return response
  }

  const formatPrice = (priceItem, item, clickable = false) => {
    //check if price_model exists
    if (!priceItem || !priceItem.price_model) {
      if (clickable) {
        return (
          <button
            onClick={() => handlePriceClick(item)}
            className="text-admin_primary hover:text-admin_primary_dk font-medium text-sm transition-colors"
          >
            + Add Price
          </button>
        )
      }
      return "No price model"
    }

    //the primary price model
    const priceModel = priceItem.price_model.find((c) => c.primary)

    if (!priceModel) {
      if (clickable) {
        return (
          <button
            onClick={() => handlePriceClick(item)}
            className="text-admin_primary hover:text-admin_primary_dk font-medium text-sm transition-colors"
          >
            + Add Price
          </button>
        )
      }
      return "No price model"
    }

    const priceContent = (() => {
      // Check if the price is free
      if (parseFloat(priceModel.amount) === 0) {
        return <div className="font-semibold">Free</div>
      }

      let price = priceDisplayFormat(priceModel)

      if (price) {
        return <div className="font-semibold">{price}</div>
      }

      return "Invalid currency"
    })()

    if (clickable) {
      return (
        <button
          onClick={() => handlePriceClick(item)}
          className="text-admin_primary hover:bg-admin_primary hover:text-white font-semibold rounded-full px-4 py-2 transition-colors min-w-[120px] inline-flex items-center justify-center"
        >
          {priceContent}
        </button>
      )
    }

    return priceContent
  }

  const productItemRender = [
    {
      id: "name",
      title: "Product Name",
      headerClassName: "w-1/4",
      sortable: true,
      render: (value, item) => (
        <button
          onClick={() => handleProductClick(item)}
          className="flex items-center gap-2 text-admin_primary hover:text-admin_primary/80 font-semibold text-left transition-colors"
        >
          {item.thumbnail && (
            <img
              src={url(item.thumbnail?.url || item.thumbnail)}
              alt={item.name}
              className="w-8 h-8 object-cover rounded"
            />
          )}
          {item.name}
        </button>
      ),
    },
    {
      id: "price_model",
      title: "Price",
      headerClassName: "w-[150px]",
      sortable: true,
      render: (value, item) => {
        return (
          <div className="flex flex-col gap-1">
            <div className="flex flex-col justify-center gap-2">
              <div className="">{formatPrice(item.price, item, true)}</div>
            </div>
          </div>
        )
      },
    },
    //add the Type field
    {
      id: "type",
      title: "Type",
      headerClassName: "w-[140px]",
      sortable: true,
      render: (value, item) => {
        const typeColor = getProductTypeColor(item.type)

        return (
          <span className={twMerge("inline-flex items-center justify-center text-xs px-2.5 py-0.5 rounded-full min-w-[110px]", typeColor)}>
            {item.type}
          </span>
        )
      },
    },
    {
      id: "platforms",
      title: "Linked with Store",
      headerClassName: "w-[110px] whitespace-nowrap",
      render: (value, item) => {
        const { price } = item

        // If no price, show empty state
        if (!price || !price.price_model || !Array.isArray(price.price_model)) {
          return (
            <div className="text-admin_text_secondary text-sm italic">
              No price configured
            </div>
          )
        }

        // Count price models by platform (check for any price model with platform IDs)
        const platformCounts = {}
        price.price_model
          .filter((priceModel) => priceModel.real_product_id && priceModel.real_product_id.length > 0)
          .forEach((priceModel) => {
            priceModel.real_product_id.forEach((p) => {
              const platform = typeof p.platform === 'object' ? p.platform?.value : p.platform
              if (!platform) return
              platformCounts[platform] = (platformCounts[platform] || 0) + 1
            })
          })

        const platforms = Object.keys(platformCounts).sort((a, b) => a.localeCompare(b))

        // If no platforms configured, show message
        if (platforms.length === 0) {
          return (
            <div className="text-admin_text_secondary text-sm italic">
              No platforms configured
            </div>
          )
        }

        return (
          <div className="flex flex-row gap-2">
            {platforms.map((platform, index) => {
              const count = platformCounts[platform]
              return (
                <div key={platform || index} className="relative">
                  <Tooltip content={`${count} ${platform} Price ${count === 1 ? 'ID' : 'IDs'}`}>
                    <div className="relative">
                      {renderPlatformIcon(platform, "w-7 h-7 text-admin_text")}
                      {count > 1 && (
                        <span className="absolute -top-1 -right-1 bg-admin_error text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                          {count}
                        </span>
                      )}
                    </div>
                  </Tooltip>
                </div>
              )
            })}
          </div>
        )
      },
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[80px] text-center",
      render: (value, item) => (
        <div className="flex items-center justify-center gap-1">
          <Button
            variant="admin_icon"
            onClick={() => handleItemDelete(item)}
          >
            <TrashIcon className="w-5 h-5 items-center" />
          </Button>
        </div>
      ),
    },
  ]

  const headerControls = (
      <div className="w-full flex items-center gap-3 -mt-5">
        <DropDownSelect
          variant="admin"
          options={productTypeOptions}
          multiSelection
          fixedWidth="160px"
          placeholder="Filter by type"
          value={typeFilter}
          onChange={(value) => {
            setTypeFilter(value)
            setRefreshKey((prev) => prev + 1)
          }}
        />

        <DropDownSelect
          variant="admin"
          options={[
            { value: "iOS", name: "iOS" },
            { value: "Android", name: "Android" },
            { value: "Web", name: "Web" },
            { value: "Windows", name: "Windows" },
            { value: "Mac", name: "Mac" },
          ]}
          multiSelection
          fixedWidth="180px"
          placeholder="Filter by platform"
          value={platformFilter}
          onChange={(value) => {
            setPlatformFilter(value)
            setRefreshKey((prev) => prev + 1)
          }}
          showCheckbox={false}
          showSelectionTick={true}
          onItemRender={(platform) => (
            <div className="flex items-center gap-2">
              {renderPlatformIcon(platform.value.toLowerCase(), "w-4 h-4 text-admin_text_secondary")}
              <span className="text-sm text-admin_text">
                {platform.name}
              </span>
            </div>
          )}
          onValueItemRender={(platform) => {
            if (!platform) return "Filter by platform"
            return (
              <div className="flex items-center gap-2">
                {renderPlatformIcon(platform.value.toLowerCase(), "w-4 h-4 text-admin_text_secondary")}
                <span className="text-sm">
                  {platform.name}
                </span>
              </div>
            )
          }}
        />
      </div>
  )

  // Field validators are now defined in the StoreProduct model definition
  // See: aldoo-ra/CMSModels/StoreProduct/index.js
  const validators = null

  return (
    <div className="p-4">
      {/* Tittle and Buttons */}
      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
          className="ml-4"
        >
          Products
        </Typography>

        <div className="flex gap-2 items-center">
          <Button
            variant="admin_secondary"
            onClick={() => setShowRestoreDialog(true)}
          >
            <div className="flex items-center gap-2">
              <ArrowUturnLeftIcon className="w-5 h-5" />
              Restore
            </div>
          </Button>

          <Button
            variant="admin_primary"
            className="whitespace-nowrap"
            onClick={createNewProduct}
          >
              <PlusCircleIcon className="w-5 h-5 mr-2" />
              Add
          </Button>
        </div>
      </div>

      <DataTable
        variant="admin_browser"
        preferredHeight="70vh"
        apiMethod={GetStoreProducts}
        headerControls={headerControls}
        itemHeight="h-[65px] xl:h-[56px]"
        searchField="name pid"
        searchObject={{}}
        requestFields={{
          match: {
            $and: [
              {
                deleted: false,
              },
              getTypeFilter(),
              getPlatformFilter(),
            ].filter(condition => Object.keys(condition).length > 0),
          },
        }}
        sort={{ created: -1 }}
        renders={productItemRender}
        refreshKey={refreshKey}
        itemsPerPage={50}
        localUserPreferencesKey="ui.storeProducts"
        styleOverrides={{
          table: "w-full table-fixed",
          searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1"
        }}
        noData={
          <Typography variant="admin_body">No products added yet</Typography>
        }
      />

      <Modal
        isOpen={selectedInstance !== null}
        onClose={() => setSelectedInstance(null)}
        variant="admin_compact"
        styleOverrides={{
          container: "bg-admin_paper h-[100vh] lg:h-[80vh] overflow-y-auto md:rounded-md border border-admin_bordered shadow-lg w-full lg:max-w-[1000px] m-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
          content: "pb-3 sm:pb-4 px-4"
        }}
      >
        <ContentEditor
          title={selectedInstance?.name || "Store Product"}
          validators={validators}
          formatters={[StoreProductFormatter]}
          contentType={{
            ...StoreProduct,
            label: selectedInstance?.pid ? (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(selectedInstance.pid)
                  showNotification({
                    message: "Product ID copied to clipboard",
                    className: "bg-admin_success text-white",
                  })
                }}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-admin_primary/10 text-admin_primary hover:bg-admin_primary/20 transition-colors cursor-pointer mt-1"
              >
                {selectedInstance.pid}
              </button>
            ) : StoreProduct?.label
          }}
          dataItem={selectedInstance}
          fieldValues={selectedInstance || {}}
          showSearch={false}
          onError={(errors) => {
            showNotification({
              message: Object.values(errors).find((e) => e),
              className: "bg-admin_error text-white",
            })
          }}
          onSave={handleItemSave}
          onClickSave={(result) => {
            //the save wasn't successful, don't close the modal
            if (!result) return
            setSelectedInstance(null)
          }}
        />
      </Modal>

      <Modal
        isOpen={selectedPrice !== null}
        onClose={() => {
          setSelectedPrice(null)
        }}
        variant="admin"
        closeOnEscape
      >
        <ContentEditor
          key={selectedPrice?._id || 'new-price'}
          title={(() => {
            const productName = typeof selectedPrice?._parentProduct?.name === 'object'
              ? (selectedPrice._parentProduct.name.en || selectedPrice._parentProduct.name.default || Object.values(selectedPrice._parentProduct.name)[0])
              : selectedPrice?._parentProduct?.name || 'Price Configuration'
            return productName
          })()}
          contentType={StorePrice}
          dataItem={selectedPrice}
          fieldValues={selectedPrice || {}}
          showSearch={false}
          showExport={true}
          showCopyPaste={true}
          onError={(errors) => {
            const errorMessage = Object.values(errors).find((e) => e)
            if (errorMessage) {
              // Parse validation error and provide user-friendly message
              let message = errorMessage
              if (message.includes('price_model') && message.includes('amount') && message.includes('required')) {
                message = "Please add a regular price with amount, currency, and type"
              }

              showNotification({
                message,
                className: "bg-admin_error text-white",
              })
            }
          }}
          onSave={handlePriceSave}
          onClickSave={async (result) => {
            // Only close modal if save was successful
            if (result) {
              setSelectedPrice(null)
            }
            // If result is null/false, keep modal open so user can fix errors
          }}
          onFieldChange={(fieldId, value) => {
            // Field change handler
          }}
        />
      </Modal>

      <StoreProductRestoreDialog
        isOpen={showRestoreDialog}
        onClose={() => setShowRestoreDialog(false)}
        onRefresh={() => setRefreshKey((prev) => prev + 1)}
      />
    </div>
  )
}
